'use client';

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Box, Tooltip, Typography } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import CommitIcon from '@mui/icons-material/Commit';
import { appEnv, gitBranch, gitRepo, gitSha } from '@/config/config';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { toggleSearchRanking } from '@/redux/slices/debugSlice';
import {
  DEV_TOOL_KEYS,
  OPEN_QA_INSPECTOR_EVENT,
  QA_INSPECTOR_STATE_EVENT,
  defaultDevChromeEnabled,
  useDevToolFlag
} from '@/lib/devToolFlags';

const dockSx = {
  position: 'fixed',
  // Mobile: sit under the navbar on the right so it does not cover
  // sticky Back / Save actions at the bottom-left of profile forms.
  top: { xs: 72, sm: 'auto' },
  right: { xs: 8, sm: 'auto' },
  left: { xs: 'auto', sm: 12 },
  bottom: { xs: 'auto', sm: 12 },
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  minWidth: { xs: 0, sm: 188 },
  p: 0.5,
  borderRadius: '8px',
  border: '1px solid rgba(15, 23, 42, 0.35)',
  backgroundColor: 'rgba(15, 23, 42, 0.92)',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.28)'
} as const;

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  width: '100%',
  m: 0,
  px: 0.75,
  py: 0.5,
  border: 'none',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  color: '#e2e8f0',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  }
} as const;

function TooltipBody({ title, body }: { title: string; body: ReactNode }) {
  return (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, display: 'block', color: '#f8fafc' }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{ color: '#cbd5e1', mt: 0.25 }}
      >
        {body}
      </Typography>
    </Box>
  );
}

function Row({
  icon,
  label,
  hint,
  active,
  activeColor = '#fbbf24',
  onClick
}: {
  icon: ReactNode;
  label: string;
  hint: ReactNode;
  active?: boolean;
  activeColor?: string;
  onClick: () => void;
}) {
  return (
    <Tooltip title={hint} placement="right" enterDelay={200}>
      <Box component="button" type="button" onClick={onClick} sx={rowSx}>
        <Box
          sx={{
            display: 'flex',
            color: active ? activeColor : '#94a3b8',
            '& svg': { fontSize: 14 }
          }}
        >
          {icon}
        </Box>
        <Typography
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline' },
            fontSize: 11,
            lineHeight: 1.2,
            fontWeight: 600,
            color: '#f8fafc',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function DevToolsDock() {
  const dispatch = useAppDispatch();
  const rankingOn = useAppSelector((state) => state.debug.searchRankingEnabled);
  const showNetwork = useDevToolFlag(
    DEV_TOOL_KEYS.network,
    defaultDevChromeEnabled()
  );
  const showRanking = useDevToolFlag(
    DEV_TOOL_KEYS.searchRanking,
    defaultDevChromeEnabled()
  );
  const showCommit = useDevToolFlag(
    DEV_TOOL_KEYS.commit,
    defaultDevChromeEnabled()
  );
  const [copied, setCopied] = useState(false);
  const [networkOpen, setNetworkOpen] = useState(false);
  const [requestCount, setRequestCount] = useState(0);

  useEffect(() => {
    const onState = (event: Event) => {
      const detail = (
        event as CustomEvent<{ open: boolean; requestCount: number }>
      ).detail;
      if (!detail) return;
      setNetworkOpen(detail.open);
      setRequestCount(detail.requestCount);
    };
    window.addEventListener(QA_INSPECTOR_STATE_EVENT, onState);
    return () => window.removeEventListener(QA_INSPECTOR_STATE_EVENT, onState);
  }, []);

  const sha = gitSha || 'local';
  const shortSha = sha === 'local' ? 'local' : sha.slice(0, 7);
  const envLabel = (appEnv || 'local').toLowerCase();
  const branchLabel = gitBranch || envLabel;
  const commitUrl =
    gitRepo && sha !== 'local'
      ? `https://github.com/${gitRepo}/commit/${sha}`
      : undefined;

  const copyCommit = async () => {
    try {
      await navigator.clipboard.writeText(sha);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      if (commitUrl) window.open(commitUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!showNetwork && !showRanking && !showCommit) return null;

  return (
    <Box sx={dockSx} aria-label="Developer tools">
      {showNetwork && (
        <Row
          icon={<BugReportIcon />}
          label="Network"
          active={networkOpen}
          activeColor="#38bdf8"
          hint={
            <TooltipBody
              title="Network Telemetry"
              body={
                <>
                  {requestCount} requests captured
                  <br />
                  {networkOpen ? 'Click to close' : 'Click to open'}
                </>
              }
            />
          }
          onClick={() =>
            window.dispatchEvent(new Event(OPEN_QA_INSPECTOR_EVENT))
          }
        />
      )}
      {showRanking && (
        <Row
          icon={<TroubleshootIcon />}
          label="Search ranking debug"
          hint={
            <TooltipBody
              title="Search Ranking Debug"
              body={
                rankingOn
                  ? 'Enabled — click to disable'
                  : 'Disabled — click to enable'
              }
            />
          }
          active={rankingOn}
          onClick={() => dispatch(toggleSearchRanking())}
        />
      )}
      {showCommit && (
        <Row
          icon={<CommitIcon />}
          label={`${shortSha} · ${branchLabel}`}
          hint={
            <TooltipBody
              title={copied ? 'Copied SHA' : 'Deployed commit'}
              body={
                <>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                      wordBreak: 'break-all',
                      color: '#f8fafc'
                    }}
                  >
                    {sha}
                  </Box>
                  {branchLabel}
                  {envLabel !== branchLabel ? ` · ${envLabel}` : ''}
                  {' · click to copy'}
                </>
              }
            />
          }
          onClick={copyCommit}
        />
      )}
      <Box
        component={Link}
        href="/internal/feature-flags"
        sx={{
          ...rowSx,
          display: { xs: 'none', sm: 'flex' },
          textDecoration: 'none',
          color: '#94a3b8',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase'
        }}
      >
        Manage flags
      </Box>
    </Box>
  );
}
